import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AT_CENTRE,
  clampScroll,
  clampZoom,
  fromShare,
  keepUnderFingers,
  MIN_ZOOM,
  pointShare,
  spread,
  toShare,
  type ZoomState,
} from './zoom'

/**
 * Приближение доски щипком и панорама прокруткой.
 *
 * Зачем вообще: доска — 25 клеток в ширину, и на телефоне одна выходит около
 * 13 пикселей. Расширенные зоны нажатия (см. BoardView) помогают попадать, но
 * разглядеть, где чья фишка, они не помогают — а увеличить доску нечем, она
 * уже занимает всю ширину экрана.
 *
 * Панорама — НАТИВНОЙ прокруткой контейнера, а не своим перетаскиванием.
 * Причина не в экономии кода: своё перетаскивание пришлось бы отличать от
 * нажатия по клетке порогом сдвига, и промах пальцем со сдвигом чуть больше
 * порога переставал бы считаться ходом. У браузера же прокрутка и нажатие
 * разведены изначально, плюс инерция и отбой у краёв достаются бесплатно.
 *
 * Своим остаётся только щипок: двумя пальцами браузер масштабирует страницу
 * целиком, а нам нужна доска внутри неё.
 */

/**
 * Где приближение вообще нужно.
 *
 * Только на узком экране. Там доска занимает всю ширину, клетка выходит около
 * 13 пикселей и пальцем в неё не попасть; на мониторе доска ограничена высотой
 * окна, клетка втрое крупнее, и возить поле мышью — работа без смысла.
 *
 * Порог тот же, по которому в стилях расходятся раскладки (60rem): иначе
 * приближение включалось бы там, где панель игроков ещё стоит в колонке, и
 * полноэкранное окно накрывало бы её.
 */
const ONLY_NARROW = '(max-width: 60rem)'

function narrowNow(): boolean {
  /*
   * Проверяем и саму функцию: в тестовой среде (jsdom) её нет, и без проверки
   * экран партии падал при первом же рендере. Нет способа узнать ширину —
   * считаем приближение недоступным: не включать непроверяемое безопаснее.
   */
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false

  return window.matchMedia(ONLY_NARROW).matches
}

/** Середина щипка в координатах видимой части контейнера. */
function midpoint(touches: TouchList, box: DOMRect): { x: number; y: number } {
  const [a, b] = [touches[0], touches[1]]

  return {
    x: (a.clientX + b.clientX) / 2 - box.left,
    y: (a.clientY + b.clientY) / 2 - box.top,
  }
}

function pointsOf(touches: TouchList): [{ x: number; y: number }, { x: number; y: number }] {
  return [
    { x: touches[0].clientX, y: touches[0].clientY },
    { x: touches[1].clientX, y: touches[1].clientY },
  ]
}

export function useBoardZoom() {
  /*
   * Окно панорамы — в состоянии, а не в ref.
   *
   * Эффекты ниже навешивают слушатели на этот элемент, и им нужно узнать, что
   * он появился. С ref этого не происходило: партия всегда начинается с лобби,
   * где окна ещё нет, эффект отрабатывал на null, а сам ref не меняется — то
   * есть переподписки не случалось никогда. Слушателей не было вовсе, и щипок
   * доставался браузеру, который масштабировал страницу целиком.
   */
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  /*
   * Масштаб — в состоянии, потому что от него зависит ширина зумируемого
   * слоя, то есть разметка. Положение прокрутки состоянием НЕ делается: им
   * управляет браузер, и дублировать его в React значило бы гонять рендер
   * на каждый кадр прокрутки.
   */
  /*
   * Партия всегда открывается целой доской.
   *
   * Масштаб между заходами не хранится нарочно. Приближение — это взгляд на
   * часть поля, и начинать с оставленного в прошлый раз значит не видеть
   * расстановки: игрок заходит и не понимает, где он и что случилось за это
   * время. Вернуть обзор стоит одного щипка, а потерянный контекст щипком
   * не вернуть.
   */
  const [zoom, setZoom] = useState(MIN_ZOOM)
  /*
   * Приближение доступно или нет — в состоянии, потому что окно можно
   * растянуть мышью, и тогда его надо выключить (и наоборот).
   */
  const [narrow, setNarrow] = useState(narrowNow)

  /** Щипок в процессе: с чего начали. */
  const pinch = useRef<{ gap: number; zoom: number } | null>(null)
  /** Последнее известное положение — его и сохраняем, без рендера. */
  const at = useRef<ZoomState>(AT_CENTRE)

  const remember = useCallback(() => {
    if (box === null) return

    at.current = {
      zoom,
      atX: toShare(box.scrollLeft, box.scrollWidth - box.clientWidth),
      atY: toShare(box.scrollTop, box.scrollHeight - box.clientHeight),
    }
  }, [box, zoom])

  /*
   * Сохранённое положение восстанавливается ОДИН РАЗ — когда окно появилось.
   *
   * Раньше эффект висел ещё и на масштабе и переставлял прокрутку после каждого
   * рендера. Положение он брал из доли, посчитанной прогнозом до применения
   * новых размеров, — и поле само уезжало на каждом шаге приближения. Теперь
   * прокрутку во время жеста ставит только zoomTo, по фактическим размерам.
   */
  useEffect(() => {
    if (box === null) return

    const saved = at.current

    box.scrollLeft = fromShare(saved.atX, box.scrollWidth - box.clientWidth)
    box.scrollTop = fromShare(saved.atY, box.scrollHeight - box.clientHeight)
  }, [box])

  /**
   * Приблизить до next, оставив точку под пальцами (или под курсором) на месте.
   *
   * Одна на все три жеста: щипок на экране, щипок на трекпаде в Safari и
   * Ctrl с колесом. Отличаются они только тем, откуда берутся next и точка.
   *
   * Прокрутка ставится сразу, не дожидаясь рендера: запас ещё старый, поэтому
   * clampScroll не даст уехать за край, а на следующем кадре восстановление
   * по доле доведёт положение до нужного.
   */
  const zoomTo = useCallback(
    (next: number, point: { x: number; y: number }): void => {
      if (box === null) return

      if (next === at.current.zoom) return

      /*
       * Сначала запоминаем, В КАКОЙ ТОЧКЕ ДОСКИ палец — долей от её размера.
       *
       * Долей, а не отношением масштабов. Отношение верно лишь тогда, когда
       * область просмотра не меняется, — а она меняется: при входе
       * в приближение окно из блока в потоке становится полноэкранным. Доска
       * тогда скачком вырастала в разы от одного микрощипка, потому что её
       * ширина считается в процентах от окна.
       */
      const shareX = pointShare(box.scrollLeft, point.x, box.scrollWidth)
      const shareY = pointShare(box.scrollTop, point.y, box.scrollHeight)

      /*
       * Пока жест идёт, масштаб пишется прямо в DOM, а состояние React не
       * трогается вовсе.
       *
       * Иначе на каждый кадр щипка перерисовывался весь экран партии вместе
       * с доской — а это больше двухсот узлов SVG, — и приближение заметно
       * дёргалось. Состояние догоняет один раз, по отпусканию пальцев
       * (см. settle): от него зависит только ширина слоя, которую мы здесь
       * уже поставили, и кнопка «вся доска».
       */
      const layer = box.firstElementChild
      const zoomed = next > MIN_ZOOM

      if (layer instanceof HTMLElement) {
        /*
         * База — ширина доски ДО того, как окно стало полноэкранным.
         *
         * Ширина слоя задана в долях базы (см. .board-zoom): считай её от
         * окна, и первый же микрощипок увеличивал бы доску в разы, потому что
         * окно меняет размер вместе с классом ниже. Замеряется здесь, пока
         * класса ещё нет; при возврате к целой доске снимается, чтобы доска
         * снова тянулась за колонкой.
         */
        if (zoomed && at.current.zoom === MIN_ZOOM) {
          layer.style.setProperty('--board-base', `${box.clientWidth}px`)
        }

        if (!zoomed) layer.style.removeProperty('--board-base')

        layer.style.setProperty('--zoom', String(next))
      }

      /*
       * Классы окна — тоже сразу, а не по состоянию.
       *
       * Прокрутка живёт на них: пока класс не выставлен, у окна нет ни высоты,
       * ни overflow, и присвоение scrollLeft ничего не делает. По состоянию
       * класс появлялся бы только после отпускания пальцев, и первый щипок
       * уводил бы доску из-под них. React на следующем рендере выставит ровно
       * те же классы — рассинхрона не будет.
       */
      box.classList.toggle('board-pan--zoomed', zoomed)
      box.parentElement?.classList.toggle('board-wrap--zoomed', zoomed)

      /*
       * Прокрутка считается по УЖЕ применённым размерам: чтение scrollWidth
       * заставляет браузер пересчитать раскладку здесь же, поэтому значения
       * ниже — настоящие, а не прогноз. Прогноз и был причиной того, что поле
       * уезжало само: он расходился с фактом, и разницу потом «доправлял»
       * эффект после рендера.
       */
      const roomX = box.scrollWidth - box.clientWidth
      const roomY = box.scrollHeight - box.clientHeight
      const toX = clampScroll(keepUnderFingers(shareX, point.x, box.scrollWidth), roomX)
      const toY = clampScroll(keepUnderFingers(shareY, point.y, box.scrollHeight), roomY)

      box.scrollLeft = toX
      box.scrollTop = toY

      at.current = { zoom: next, atX: toShare(toX, roomX), atY: toShare(toY, roomY) }
    },
    [box],
  )

  /**
   * Свести состояние с тем, что уже нарисовано, и запомнить.
   *
   * Зовётся по концу жеста. setZoom здесь ничего не перерисовывает заново —
   * ширина слоя и прокрутка уже стоят, — но без него React не знает про новый
   * масштаб, и следующий же рендер вернул бы прежнюю ширину.
   */
  const settle = useCallback(() => {
    setZoom(at.current.zoom)
  }, [])

  // Щипок. Своими слушателями, а не через React: тому нужен passive: false,
  // чтобы preventDefault отменил масштабирование страницы целиком, а React
  // ставит touch-слушатели пассивными.
  useEffect(() => {
    if (box === null || !narrow) return

    const start = (event: TouchEvent): void => {
      if (event.touches.length !== 2) return

      const [a, b] = pointsOf(event.touches)

      pinch.current = { gap: spread(a, b), zoom: at.current.zoom }
    }

    const move = (event: TouchEvent): void => {
      const from = pinch.current

      if (from === null || event.touches.length !== 2) return

      // Отменяем масштабирование страницы: масштабируем доску.
      event.preventDefault()

      const [a, b] = pointsOf(event.touches)
      const gap = spread(a, b)

      if (from.gap === 0) return

      zoomTo(
        clampZoom((from.zoom * gap) / from.gap),
        midpoint(event.touches, box.getBoundingClientRect()),
      )
    }

    const end = (event: TouchEvent): void => {
      if (event.touches.length >= 2) return

      pinch.current = null
      settle()
    }

    box.addEventListener('touchstart', start, { passive: true })
    box.addEventListener('touchmove', move, { passive: false })
    box.addEventListener('touchend', end, { passive: true })
    box.addEventListener('touchcancel', end, { passive: true })

    return () => {
      box.removeEventListener('touchstart', start)
      box.removeEventListener('touchmove', move)
      box.removeEventListener('touchend', end)
      box.removeEventListener('touchcancel', end)
    }
  }, [box, narrow, zoomTo, settle])

  /*
   * Приближение без сенсорного экрана.
   *
   * На Safari под macOS щипок на трекпаде приходит проприетарными
   * gesture-событиями со готовым множителем в scale, а на Chrome и Firefox —
   * колесом с зажатым ctrlKey (так браузеры сообщают именно щипок, а не
   * прокрутку). Поддержаны оба: без них зум не работал на настольном браузере
   * вовсе — touch-события там не возникают.
   *
   * preventDefault обязателен в обоих: иначе браузер приблизит страницу
   * целиком вместо доски.
   */
  useEffect(() => {
    if (box === null || !narrow) return

    const wheel = (event: WheelEvent): void => {
      if (!event.ctrlKey) return

      event.preventDefault()

      const rect = box.getBoundingClientRect()

      /*
       * Сотая доля прокрутки на шаг — подобрано так, чтобы обычный щипок
       * на трекпаде проходил весь диапазон за одно движение, а колесо мыши
       * с Ctrl не перескакивало от края к краю за один щелчок.
       */
      zoomTo(clampZoom(at.current.zoom * (1 - event.deltaY / 100)), {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      })

      /* У колеса нет «отпускания», поэтому состояние сводится сразу: шагов
         тут единицы, а не кадр за кадром, как в щипке. */
      settle()
    }

    /** Начальный масштаб жеста: gesturechange даёт множитель от его начала. */
    let gestureFrom = at.current.zoom

    const gestureStart = (event: Event): void => {
      event.preventDefault()
      gestureFrom = at.current.zoom
    }

    const gestureChange = (event: Event): void => {
      event.preventDefault()

      // scale — из проприетарного GestureEvent, которого нет в типах DOM.
      const scale = (event as Event & { scale?: number }).scale

      if (scale === undefined) return

      const rect = box.getBoundingClientRect()
      const point = event as Event & { clientX?: number; clientY?: number }

      zoomTo(clampZoom(gestureFrom * scale), {
        x: (point.clientX ?? rect.left + rect.width / 2) - rect.left,
        y: (point.clientY ?? rect.top + rect.height / 2) - rect.top,
      })
    }

    const done = (): void => settle()

    box.addEventListener('wheel', wheel, { passive: false })
    box.addEventListener('gesturestart', gestureStart, { passive: false })
    box.addEventListener('gesturechange', gestureChange, { passive: false })
    box.addEventListener('gestureend', done, { passive: true })

    return () => {
      box.removeEventListener('wheel', wheel)
      box.removeEventListener('gesturestart', gestureStart)
      box.removeEventListener('gesturechange', gestureChange)
      box.removeEventListener('gestureend', done)
    }
  }, [box, narrow, zoomTo, settle])

  /*
   * Окно перетащили через порог — приближение включается или выключается.
   *
   * При выключении обязателен сброс: иначе на широком экране осталась бы
   * приближённая доска, которую нечем вернуть — жесты уже не слушаются.
   */
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const watch = window.matchMedia(ONLY_NARROW)
    const react = (): void => setNarrow(watch.matches)

    watch.addEventListener('change', react)

    return () => watch.removeEventListener('change', react)
  }, [])

  /** Вернуть доску целиком — единственный способ выйти из приближения. */
  const reset = useCallback(() => {
    at.current = AT_CENTRE

    /* Снимаем то, что выставлено в обход React: базу, масштаб и классы окна. */
    const layer = box?.firstElementChild

    if (layer instanceof HTMLElement) {
      layer.style.removeProperty('--board-base')
      layer.style.removeProperty('--zoom')
    }

    box?.classList.remove('board-pan--zoomed')
    box?.parentElement?.classList.remove('board-wrap--zoomed')

    setZoom(MIN_ZOOM)
  }, [box])

  /*
   * Приближение стало недоступно — возвращаем целую доску.
   *
   * Без этого растянутое окно осталось бы с приближённым полем, вернуть
   * которое нечем: жесты на широком экране не слушаются, а кнопка «вся доска»
   * — единственный выход — живёт внутри того же приближения.
   */
  useEffect(() => {
    if (!narrow && zoom > MIN_ZOOM) reset()
  }, [narrow, zoom, reset])

  /** pan — ref-колбэк для окна панорамы: он и поднимает подписку на жесты. */
  return { pan: setBox, zoom, reset, remember }
}
